// Ported from dev-scrips-pm-react src/shared/lib/pdf-response.ts (PROD-1119 cross-module closure).
/**
 * Normalise a "print" endpoint response into a browser-openable PDF URL.
 *
 * The Scrips .NET print endpoints (billing `PrintInvoice` / `PrintReceipts`,
 * patients `PVSPrint`) historically return ONE of two shapes — and which one
 * varies by endpoint/deploy, so we handle both defensively (this is the same
 * logic proven out by the PVS flow, `use-post-visit-summary.ts`):
 *
 *   1. An **HTTP URL string** (Azure Blob SAS link) — used directly.
 *   2. **base64-encoded PDF bytes** (Flutter decodes these to `bytes` and shows
 *      them, e.g. `PrintInvoiceState(bytes)`). We decode to a `Blob` and mint a
 *      same-origin `blob:` object URL.
 *
 * Both are JSON-quoted on the wire (the controller serialises a bare `string`),
 * so callers that went through `get<string>` (which `JSON.parse`s) already have
 * the unquoted value; we strip residual quotes + whitespace defensively anyway.
 *
 * A `blob:` URL is **same-origin**, which matters for printing: an `<iframe>`
 * showing a blob PDF can be printed via `contentWindow.print()`, whereas a
 * cross-origin SAS URL cannot. Callers should revoke the returned `blob:` URL
 * when done (see `isBlob`).
 */
export interface ResolvedPdf {
  url: string;
  /** true when `url` is a `blob:` object URL the caller must revoke. */
  isBlob: boolean;
}

function stripQuotes(raw: string): string {
  return raw.replace(/^"+|"+$/g, '');
}

function base64ToBytes(b64: string): Uint8Array<ArrayBuffer> {
  const binary = atob(b64);
  const buffer = new ArrayBuffer(binary.length);
  const out = new Uint8Array(buffer);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
}

export function resolvePdfResponseToUrl(raw: string): ResolvedPdf {
  const value = stripQuotes(raw.trim());
  if (!value) {
    throw new Error('Print: server returned an empty response.');
  }
  if (/^https?:\/\//i.test(value)) {
    return { url: value, isBlob: false };
  }
  // Treat as base64 PDF bytes. `atob` throws on malformed input — the caller's
  // onError surfaces that via the global toast.
  const blob = new Blob([base64ToBytes(value)], { type: 'application/pdf' });
  return { url: URL.createObjectURL(blob), isBlob: true };
}
